// Loads the latest advisor plan from sessionStorage-backed plan store and
// fetches /advisor/sessions/:id. All plan subpages share this hook so the
// hub generates once and each drill-in re-reads from the same react-query
// cache (no duplicate fetches).
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { usePlanStore } from '@/stores/plan';
import type { BudgetAlert } from '@/components/advisor/BudgetAlerts';
import type { CashflowAlert } from '@/components/advisor/CashflowAlerts';

export interface Recommendation {
  title: string;
  detail: string;
  action_category: string | null;
  monthly_impact_aud: number;
  priority: number;
}

export interface GoalProjection {
  goal_id: string;
  expected_completion: string | null;
  on_track: boolean;
  gap_per_month: number;
}

export interface PlanSession {
  stale?: boolean;
  money_revision?: number;
  current_money_revision?: number;
  id: string;
  generated_at: number;
  model: string;
  language: string;
  assessment: string;
  recommendations: Recommendation[];
  goal_projections: GoalProjection[];
  risks: string[];
  budget_alerts: BudgetAlert[];
  cashflow_alerts: CashflowAlert[];
}

export function useLatestPlan() {
  const ws = useWorkspaceStore((s) => s.current);
  const planId = usePlanStore((s) => s.currentPlanId);

  const query = useQuery({
    queryKey: ['advisor', 'session', planId, ws?.id],
    queryFn: () => api.get<PlanSession>(`/advisor/sessions/${planId}`),
    enabled: !!ws && !!planId,
    staleTime: 5 * 60 * 1000,
  });
  // Historical sessions remain available in History. Superseded amounts must
  // not keep driving the current-plan surfaces after a receipt cost changes.
  return { ...query, data: query.data?.stale ? undefined : query.data, stalePlan: query.data?.stale === true };
}
